import React, { useEffect, useImperativeHandle, useMemo, useRef, useState, forwardRef } from 'react';
import { Button, Input, Modal, Typography, message } from 'antd';
import { fetchSqlData } from '../../services/api';
import { useLocation } from 'react-router-dom';
import { dccCopy, dccDelete, dccDownloadUrl, dccMkdir, dccMove, dccUpload } from '../../services/dccApi';
import FilePreviewModal from './FilePreviewModal';
import SelectDCCFileModal from '../modals/SelectDCCFileModal';

interface FileUploadToolProps {
  onSuccess?: (response: any) => void;
  onError?: (error: any) => void;
  buttonText?: string;
  resetTrigger?: any;
  tempSubpath?: string;
  finalizeSubfolder?: string;
}

interface UploadedFile {
  name: string;
  status: string;
  isRenaming: boolean;
  renameValue: string;
}

export interface FileUploadToolHandle {
  finalizeUpload: (doc_refnum: string) => Promise<{ success: boolean; folderPath?: string; movedFiles?: string[]; error?: string }>;
  hasFilesToUpload: () => boolean;
  getUploadedFileNames: () => string[];
  reset: (args?: { deleteTemp?: boolean }) => Promise<void>;
}

const cleanPath = (path: string): string => {
  let p = path;
  const parts = p.split('/').filter(Boolean);
  if (parts.length >= 2) {
    const prev = parts[parts.length - 2];
    if (prev === 'edit' || prev === 'view' || prev === 'create') {
      parts.pop();
      p = '/' + parts.join('/');
    }
  }
  return p;
};

const FileUploadTool = forwardRef<FileUploadToolHandle, FileUploadToolProps>(
  ({ onSuccess, onError, buttonText = 'Upload', resetTrigger, tempSubpath, finalizeSubfolder }, ref) => {
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [folderPath, setFolderPath] = useState<string>('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [isDccPickerOpen, setIsDccPickerOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [renameTargetIdx, setRenameTargetIdx] = useState<number | null>(null);
  const [renameDraftValue, setRenameDraftValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  const token = localStorage.getItem('token');

  const resolvedTempPath = useMemo(() => {
    const user = localStorage.getItem('user');
    if (!user) return '';
    const sub = String(tempSubpath || '').trim().replace(/^[/\\]+/, '').replace(/\\/g, '/');
    return sub ? `temp/${user}/${sub}` : `temp/${user}`;
  }, [tempSubpath]);

  // Fetch destination folder path based on current route
  const fetchFolderPath = async () => {
    try {
      const currentPath = location.pathname;
      const cleanedPath = cleanPath(currentPath);
      const resp = await fetchSqlData('cor_destination_folders', 'route_path', cleanedPath);
      if (resp && resp.length > 0 && resp[0].folder_path) {
        setFolderPath(resp[0].folder_path);
        return resp[0].folder_path;
      } else {
        setFolderPath('');
        message.error('No destination folder found for this page.');
        return '';
      }
    } catch (err) {
      setFolderPath('');
      message.error('Failed to fetch destination folder.');
      return '';
    }
  };

  const reset = async (args?: { deleteTemp?: boolean }) => {
    const deleteTemp = !!args?.deleteTemp;
    const toDelete = deleteTemp
      ? uploadedFiles
          .filter((f) => f.status.includes('successful'))
          .map((f) => f.name)
      : [];

    if (deleteTemp && toDelete.length && token && resolvedTempPath) {
      for (const fname of toDelete) {
        try {
          await dccDelete(`${resolvedTempPath}/${fname}`, token);
        } catch {
          // best-effort
        }
      }
    }

    setFiles([]);
    setUploadedFiles([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const openDccPicker = () => setIsDccPickerOpen(true);

  const handleAttachDccFiles = async (items: Array<{ fullPath: string; name: string }>) => {
    if (!token) {
      message.error('Not authenticated. Please login again.');
      return;
    }
    const user = localStorage.getItem('user');
    if (!user) {
      message.error('No user found in localStorage.');
      return;
    }
    let attached = 0;
    for (const args of items) {
      try {
        const src = String(args.fullPath || '').trim();
        const dstName = String(args.name || '').trim();
        if (!src || !dstName) {
          throw new Error('Invalid file selection');
        }
        const dst = `${resolvedTempPath}/${dstName}`;
        await dccCopy(src, dst, token);
        const serverFilename = dstName;
        setUploadedFiles((prev) => {
          if (prev.some((f) => f.name === serverFilename)) return prev;
          return [
            ...prev,
            {
              name: serverFilename,
              status: 'Upload successful',
              isRenaming: false,
              renameValue: serverFilename,
            },
          ];
        });
        attached += 1;
      } catch (e: any) {
        message.error(e?.message || `Failed to attach ${args.name || 'file'}`);
      }
    }
    if (attached > 0) {
      message.success(attached === 1 ? 'File attached' : `${attached} files attached`);
      setIsDccPickerOpen(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    setFiles(selected);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setIsUploading(true);
    for (const file of files) {
      try {
        if (!resolvedTempPath) {
          throw new Error('No user found in localStorage.');
        }
        const response = await dccUpload(resolvedTempPath, [file], token);
        onSuccess?.(response);

        const serverFilename = (() => {
          const uploaded = (response as any)?.uploaded;
          if (Array.isArray(uploaded) && uploaded.length > 0) return String(uploaded[0]);
          const fn = (response as any)?.filename;
          if (fn) return String(fn);
          return file.name;
        })();

        setUploadedFiles(prev => [
          ...prev,
          {
            name: serverFilename,
            status: 'Upload successful',
            isRenaming: false,
            renameValue: serverFilename,
          },
        ]);
      } catch (error) {
        onError?.(error);
        setUploadedFiles(prev => [
          ...prev,
          {
            name: file.name,
            status: (error as any)?.message || 'Upload failed',
            isRenaming: false,
            renameValue: file.name,
          },
        ]);
      }
    }
    setFiles([]);
    if (inputRef.current) inputRef.current.value = '';
    setIsUploading(false);
  };

  const handleViewServer = (fileName: string) => {
    if (!resolvedTempPath) {
      message.error('No user found in localStorage.');
      return;
    }
    const fullPath = `${resolvedTempPath}/${fileName}`;
    const isPdf = fileName.toLowerCase().endsWith('.pdf');
    const isImage = /\.(png|jpe?g|gif|bmp|webp|svg)$/i.test(fileName);
    if (isPdf) {
      setPreviewPath(fullPath);
      setIsPreviewOpen(true);
      return;
    }
    if (isImage) {
      setPreviewPath(fullPath);
      setIsPreviewOpen(true);
      return;
    }
    const url = dccDownloadUrl(fullPath, token, true);
    window.open(url, '_blank');
  };

  const handleRename = async (fileIdx: number, draftValue: string) => {
    const file = uploadedFiles[fileIdx];
    if (!resolvedTempPath) {
      setUploadedFiles(prev => prev.map((f, idx) => idx === fileIdx ? {
        ...f,
        status: 'Rename failed (no user)',
      } : f));
      return;
    }
    try {
      const oldExt = file.name.includes('.') ? `.${file.name.split('.').pop()}` : '';
      const hasExt = String(draftValue || '').includes('.');
      const nextName = hasExt ? String(draftValue || '') : `${String(draftValue || '')}${oldExt}`;
      await dccMove(`${resolvedTempPath}/${file.name}`, `${resolvedTempPath}/${nextName}`, token);
      setUploadedFiles(prev => prev.map((f, idx) => idx === fileIdx ? {
        ...f,
        name: nextName,
        isRenaming: false,
        status: 'Rename successful',
        renameValue: nextName,
      } : f));
    } catch (e) {
      setUploadedFiles(prev => prev.map((f, idx) => idx === fileIdx ? {
        ...f,
        status: (e as any)?.message || 'Rename failed',
      } : f));
    }
  };

  const handleStartRename = (fileIdx: number) => {
    const file = uploadedFiles[fileIdx];
    setRenameTargetIdx(fileIdx);
    setRenameDraftValue(file?.name || '');
    setIsRenameModalOpen(true);
  };

  const handleCancelRename = () => {
    setIsRenameModalOpen(false);
    setRenameTargetIdx(null);
    setRenameDraftValue('');
  };

  const handleConfirmRename = async () => {
    if (renameTargetIdx === null) {
      handleCancelRename();
      return;
    }

    const next = String(renameDraftValue || '').trim();
    if (!next) {
      message.error('Please enter a file name.');
      return;
    }

    await handleRename(renameTargetIdx, next);
    handleCancelRename();
  };

  // Delete file handler
  const handleDelete = async (fileIdx: number) => {
    const file = uploadedFiles[fileIdx];
    try {
      if (!resolvedTempPath) {
        setUploadedFiles(prev => prev.map((f, idx) => idx === fileIdx ? {
          ...f,
          status: 'Delete failed (no user)',
        } : f));
        return;
      }
      await dccDelete(`${resolvedTempPath}/${file.name}`, token);
      setUploadedFiles(prev => prev.filter((_, idx) => idx !== fileIdx));
    } catch (e) {
      setUploadedFiles(prev => prev.map((f, idx) => idx === fileIdx ? {
        ...f,
        status: (e as any)?.message || 'Delete failed',
      } : f));
    }
  };

  // Finalize upload: move files from temp to final destination
  const finalizeUpload = async (
    doc_refnum: string
  ): Promise<{ success: boolean; folderPath?: string; movedFiles?: string[]; error?: string }> => {
    // Always fetch latest folderPath when finalizing
    const latestFolderPath = await fetchFolderPath();
    if (!latestFolderPath) {
      message.error('Destination folder not set.');
      return { success: false, error: 'Destination folder not set.' };
    }
    const filesToFinalize = uploadedFiles
      .filter(f => f.status.includes('successful'))
      .map(f => f.name);
    if (!filesToFinalize.length) {
      return { success: false, error: 'No uploaded files to finalize.' };
    }
    try {
      if (!resolvedTempPath) {
        message.error('No user found in localStorage.');
        return { success: false, error: 'No user found in localStorage.' };
      }

      const normalizedFolderPath = String(latestFolderPath)
        .trim()
        .replace(/^[/\\]+/, '')
        .replace(/^root[\\/]/i, '')
        .replace(/\\/g, '/');
      const safeDocRef = String(doc_refnum).trim();

      const segments = normalizedFolderPath
        .split(/[/\\]+/)
        .filter(Boolean)
        .concat(safeDocRef ? [safeDocRef] : [])
        .concat(finalizeSubfolder ? [String(finalizeSubfolder).trim()] : [])
        .filter(Boolean);

      let destinationFolder = '';
      for (const seg of segments) {
        destinationFolder = destinationFolder ? `${destinationFolder}/${seg}` : seg;
        await dccMkdir(destinationFolder, token);
      }

      for (const fname of filesToFinalize) {
        await dccMove(`${resolvedTempPath}/${fname}`, `${destinationFolder}/${fname}`, token);
      }

      return { success: true, folderPath: destinationFolder, movedFiles: filesToFinalize };
    } catch (e) {
      const msg = String((e as any)?.message || 'Failed to finalize upload.');
      message.error(msg);
      return { success: false, error: msg };
    } finally {
      setFiles([]);
      setUploadedFiles([]);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  useImperativeHandle(ref, () => ({
    finalizeUpload,
    hasFilesToUpload: () => uploadedFiles.some(f => f.status.includes('successful')),
    getUploadedFileNames: () =>
      uploadedFiles.filter((f) => f.status.includes('successful')).map((f) => f.name),
    reset,
    // Optionally expose other methods if needed
  }));

  useEffect(() => {
    setFiles([]);
    setUploadedFiles([]);
    if (inputRef.current) inputRef.current.value = '';
  }, [resetTrigger]);

  return (
    <span>
      <input
        type="file"
        style={{ display: 'none' }}
        ref={inputRef}
        multiple
        onChange={handleFileChange}
      />
      <Button
        onClick={() => inputRef.current?.click()}
        disabled={isUploading}
      >
        Select File(s)
      </Button>
      <Button
        type="default"
        onClick={openDccPicker}
        disabled={isUploading}
        style={{ marginLeft: 8 }}
      >
        Select DCC file
      </Button>
      <Button
        type="default"
        onClick={handleUpload}
        disabled={!files.length || isUploading}
        loading={isUploading}
        style={{ marginLeft: 8 }}
      >
        {buttonText}
      </Button>
      {files.length > 0 && (
        <Typography.Text style={{ marginLeft: 8 }}>
          {files.map(f => f.name).join(', ')}
        </Typography.Text>
      )}
      <br />
      {uploadedFiles.length > 0 && uploadedFiles.map((file, idx) => (
        <div key={file.name + idx} style={{ marginTop: 8 }}>
          <Button size="small" style={{ marginRight: 8 }} onClick={() => handleViewServer(file.name)}>
            View
          </Button>
          <Button size="small" style={{ marginRight: 8 }} onClick={() => handleStartRename(idx)}>
            Rename
          </Button>
          <Button size="small" danger style={{ marginRight: 8 }} onClick={() => handleDelete(idx)}>
            Delete
          </Button>
          <Typography.Text type={file.status.includes('failed') ? 'danger' : file.status.includes('successful') ? 'success' : undefined}>
            {file.name}
          </Typography.Text>
          <Typography.Text style={{ marginLeft: 8 }} type={file.status.includes('failed') ? 'danger' : file.status.includes('successful') ? 'success' : undefined}>
            {file.status}
          </Typography.Text>
        </div>
      ))}

      <Modal
        title="Rename file"
        open={isRenameModalOpen}
        onOk={handleConfirmRename}
        onCancel={handleCancelRename}
        okText="Save"
        cancelText="Cancel"
        destroyOnClose
      >
        <Input
          value={renameDraftValue}
          onChange={(e) => setRenameDraftValue(e.target.value)}
          onPressEnter={handleConfirmRename}
          autoFocus
        />
      </Modal>

      <FilePreviewModal
        open={isPreviewOpen}
        path={previewPath}
        token={token}
        enableAnnotatedUpload
        onAnnotatedUploaded={({ filename }) => {
          setUploadedFiles((prev) => {
            if (prev.some((f) => f.name === filename)) return prev;
            return [
              ...prev,
              {
                name: filename,
                status: 'Upload successful',
                isRenaming: false,
                renameValue: filename,
              },
            ];
          });
        }}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewPath(null);
        }}
      />

      <SelectDCCFileModal
        open={isDccPickerOpen}
        mode="multiple"
        onCancel={() => setIsDccPickerOpen(false)}
        onSelectMultiple={(args) => {
          handleAttachDccFiles(args);
        }}
      />
    </span>
  );
});

export default FileUploadTool;
